import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SeoHead from '../../components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://marinconstruesfuerzo.com';

const privacyJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/politica-de-privacidad`,
  url: `${siteUrl}/politica-de-privacidad`,
  name: 'Política de Privacidad | Marin Construesfuerzo SL',
  description: 'Política de privacidad de Marin Construesfuerzo SL. Información sobre el tratamiento de datos personales conforme al RGPD.',
  inLanguage: 'es-ES',
  isPartOf: { '@id': `${siteUrl}/#website` },
  publisher: {
    '@type': 'Organization',
    name: 'Marin Construesfuerzo SL',
    url: siteUrl,
  },
};

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Política de Privacidad | Marin Construesfuerzo SL"
        description="Política de privacidad de Marin Construesfuerzo SL — Tratamiento de datos personales conforme al RGPD. Empresa de construcción en Arrecife, Lanzarote."
        canonical={`${siteUrl}/politica-de-privacidad`}
        jsonLd={privacyJsonLd}
      />
      <Navbar />
      
      <main className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Política de <strong className="text-amber-500">Privacidad</strong>
          </h1>
          
          <p className="text-gray-600 mb-8">
            Última actualización: {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <div className="space-y-8 text-gray-700">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Información que Recopilamos</h2>
              <p className="mb-4">
                En <strong>Marin Construesfuerzo SL</strong>, recopilamos información personal cuando usted se pone en contacto con nosotros a través de nuestro formulario de contacto o por otros medios. La información que podemos recopilar incluye:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Nombre completo</li>
                <li>Dirección de correo electrónico</li>
                <li>Número de teléfono</li>
                <li>Información sobre el servicio de construcción que solicita</li>
                <li>Cualquier otra información que nos proporcione voluntariamente</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Uso de la Información</h2>
              <p className="mb-4">
                Utilizamos la información recopilada para los siguientes propósitos:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Responder a sus consultas y solicitudes de presupuesto</li>
                <li>Proporcionar información sobre nuestros servicios de <strong>construcción en Lanzarote</strong></li>
                <li>Mejorar nuestros servicios y la experiencia del cliente</li>
                <li>Enviar comunicaciones relacionadas con su proyecto de construcción</li>
                <li>Cumplir con obligaciones legales y regulatorias</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Base Legal para el Tratamiento</h2>
              <p className="mb-4">
                El tratamiento de sus datos personales se basa en:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Consentimiento:</strong> Al enviar el formulario de contacto, usted consiente el tratamiento de sus datos</li>
                <li><strong>Interés legítimo:</strong> Para gestionar y responder a sus solicitudes de servicios de construcción</li>
                <li><strong>Cumplimiento legal:</strong> Para cumplir con las obligaciones fiscales y contables</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Compartir Información</h2>
              <p className="mb-4">
                <strong>Marin Construesfuerzo SL</strong> no vende, alquila ni comparte su información personal con terceros, excepto en los siguientes casos:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Cuando sea necesario para prestar nuestros servicios de construcción</li>
                <li>Con proveedores de servicios que nos ayudan a operar nuestro negocio</li>
                <li>Cuando sea requerido por ley o por autoridades competentes</li>
                <li>Para proteger nuestros derechos legales</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Seguridad de los Datos</h2>
              <p>
                Implementamos medidas de seguridad técnicas y organizativas apropiadas para proteger su información personal contra acceso no autorizado, alteración, divulgación o destrucción. Sin embargo, ningún método de transmisión por Internet o almacenamiento electrónico es 100% seguro.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Retención de Datos</h2>
              <p>
                Conservamos su información personal durante el tiempo necesario para cumplir con los propósitos descritos en esta política, a menos que la ley requiera o permita un período de retención más largo. Los datos relacionados con proyectos de <strong>construcción en Arrecife, Lanzarote</strong> se conservan según las obligaciones legales aplicables.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Sus Derechos</h2>
              <p className="mb-4">
                De acuerdo con el Reglamento General de Protección de Datos (RGPD), usted tiene los siguientes derechos:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Derecho de acceso:</strong> Puede solicitar información sobre los datos personales que tenemos sobre usted</li>
                <li><strong>Derecho de rectificación:</strong> Puede solicitar la corrección de datos inexactos</li>
                <li><strong>Derecho de supresión:</strong> Puede solicitar la eliminación de sus datos personales</li>
                <li><strong>Derecho de limitación:</strong> Puede solicitar la limitación del tratamiento de sus datos</li>
                <li><strong>Derecho de portabilidad:</strong> Puede solicitar recibir sus datos en un formato estructurado</li>
                <li><strong>Derecho de oposición:</strong> Puede oponerse al tratamiento de sus datos personales</li>
              </ul>
              <p className="mt-4">
                Para ejercer estos derechos, puede contactarnos en: <a href="mailto:m.construesfuerzo@gmail.com" className="text-amber-500 hover:underline cursor-pointer">m.construesfuerzo@gmail.com</a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cookies</h2>
              <p>
                Nuestro sitio web puede utilizar cookies para mejorar la experiencia del usuario. Las cookies son pequeños archivos de texto que se almacenan en su dispositivo. Puede configurar su navegador para rechazar cookies, aunque esto puede afectar la funcionalidad del sitio.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Enlaces a Terceros</h2>
              <p>
                Nuestro sitio web puede contener enlaces a sitios web de terceros. No somos responsables de las prácticas de privacidad de estos sitios. Le recomendamos revisar las políticas de privacidad de cualquier sitio web de terceros que visite.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Menores de Edad</h2>
              <p>
                Nuestros servicios no están dirigidos a menores de 18 años. No recopilamos intencionalmente información personal de menores. Si descubrimos que hemos recopilado información de un menor, la eliminaremos de inmediato.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Cambios en esta Política</h2>
              <p>
                Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios entrarán en vigor inmediatamente después de su publicación en esta página. Le recomendamos revisar periódicamente esta política para estar informado sobre cómo protegemos su información.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Información de Contacto</h2>
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="mb-2"><strong>Responsable del tratamiento:</strong></p>
                <p className="mb-1">Marin Construesfuerzo SL</p>
                <p className="mb-1">NIF: B26637975</p>
                <p className="mb-1">Dirección: CL Victor Hugo, 91, 35500 Arrecife, Las Palmas, Lanzarote, España</p>
                <p className="mb-1">Teléfono: <a href="tel:+34613725305" className="text-amber-500 hover:underline cursor-pointer">613 72 53 05</a></p>
                <p className="mb-1">Email: <a href="mailto:m.construesfuerzo@gmail.com" className="text-amber-500 hover:underline cursor-pointer">m.construesfuerzo@gmail.com</a></p>
                <p className="mt-4">
                  Si tiene alguna pregunta sobre esta política de privacidad o sobre cómo manejamos sus datos personales, no dude en contactarnos.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Autoridad de Control</h2>
              <p>
                Si considera que el tratamiento de sus datos personales no es adecuado, tiene derecho a presentar una reclamación ante la Agencia Española de Protección de Datos (AEPD):
              </p>
              <div className="bg-gray-50 rounded-lg p-6 mt-4">
                <p className="mb-1"><strong>Agencia Española de Protección de Datos</strong></p>
                <p className="mb-1">C/ Jorge Juan, 6</p>
                <p className="mb-1">28001 Madrid</p>
                <p className="mb-1">Web: <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:underline cursor-pointer">www.aepd.es</a></p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
